import L from "leaflet";
import { Fragment } from "react";
import { renderToString } from "react-dom/server";
import { StickyNote } from "../model/viewModel/stickyNote";
import { RichText } from "../model/viewModel/richText";
import { getRichTextStyle, getRichTextFont } from "../utils/richTextFunctions";
import * as color from "../constants/colors";

export const stickyNoteIconMarker = (stickyNote: StickyNote, highlighted: boolean) => {
  const padding = 10;
  const minWidth = 50;
  const maxTextWidth = 1000;
  const maxLines = 50;
  const trimmedTexts: RichText[] = [];

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  const trimToFit = (
    richText: RichText,
    context: CanvasRenderingContext2D,
    availableWidth: number,
  ): RichText => {
    context.font = getRichTextFont(richText);
    const trimmed = { ...richText };
    trimmed.appendLinebreak = false;

    if (availableWidth <= 0 || richText.text.length < 1) {
      trimmed.text = "";
      return trimmed;
    }

    let start = 0;
    let end = richText.text.length;
    let result = "";

    while (start <= end) {
      const mid = Math.floor((start + end) / 2);
      const width = context.measureText(richText.text.slice(0, mid)).width;

      if (width <= availableWidth) {
        result = richText.text.slice(0, mid);
        start = mid + 1;
      } else {
        end = mid - 1;
      }
    }

    trimmed.text = result;
    return trimmed;
  };

  const lines = stickyNote.richTexts.reduce((resultArray: RichText[][], item) => {
    if (resultArray.length === 0) {
      resultArray.push([]);
    }
    resultArray[resultArray.length - 1].push(item);
    if (item.appendLinebreak) {
      resultArray.push([]);
    }
    return resultArray;
  }, []);

  let largestTextWidth = 0;
  if (context) {
    const elipsesRichText = new RichText("...");
    elipsesRichText.appendLinebreak = true;
    context.font = getRichTextFont(elipsesRichText);
    const ellipsisWidth = context.measureText(elipsesRichText.text).width;

    lines
      .filter((line) => line.length)
      .slice(0, maxLines)
      .forEach((line: RichText[]) => {
        let lineWidth = 0;
        let truncateLine = false;
        line.forEach((richText: RichText) => {
          if (truncateLine) {
            return;
          }
          context.font = getRichTextFont(richText);
          const spanWidth = context.measureText(richText.text).width;
          const newLineWidth = lineWidth + spanWidth;
          if (newLineWidth + ellipsisWidth >= maxTextWidth) {
            largestTextWidth = maxTextWidth;
            truncateLine = true;
          } else if (newLineWidth > largestTextWidth) {
            largestTextWidth = newLineWidth;
          }
          if (truncateLine) {
            trimmedTexts.push(
              trimToFit(richText, context, maxTextWidth - lineWidth - ellipsisWidth),
            );
            trimmedTexts.push(elipsesRichText);
          } else {
            trimmedTexts.push(richText);
          }
          lineWidth = newLineWidth;
        });
      });
  }
  if (lines.length > maxLines) {
    trimmedTexts.push(new RichText(String.fromCharCode(0x22ee))); // vertical ellipsis
  }

  largestTextWidth = Math.round(largestTextWidth);

  const width = Math.max(largestTextWidth + 2 * padding, minWidth);

  const icon = L.divIcon({
    className: "",
    iconAnchor: highlighted ? [1, 1] : [0, 0],

    html: renderToString(
      <div
        className={`sticky-note-icon-marker`}
        style={{
          backgroundColor: "lightyellow",
          opacity: 0.85,
          margin: "0px",
          width: `${width}px`,
          padding: `${padding}px`,
          border: highlighted ? `2px solid ${color.BLUE_HIGHLIGHT}` : "1px solid darkgray",
        }}
      >
        <div>
          {trimmedTexts.map((richText, index) => (
            <Fragment key={index}>
              {richText.text && <span style={getRichTextStyle(richText)}>{richText.text}</span>}
              {richText.appendLinebreak && <br />}
            </Fragment>
          ))}
        </div>
      </div>,
    ),
  });
  return icon;
};
